import { connect } from 'react-redux';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { useState, useEffect } from 'react';

import { showErrorMessage } from '@actions/messageconfirmation';

import { getTheme } from '@commons/utils/theme';
import { ListView } from '@commons/utils/styledLibraryComponents';
import { LISTVIEW_NO_TOP_PADDING } from '@commons/constants/listViewProps';
import EmptyState from '@commons/EmptyState';

import { usePeriodDatePickerState } from '@hooks/usePeriodDatePickerState';

import invoiceService from '@services/invoice';

import { getClientInfo } from '@selectors/client';

import DeepsightFiltersButton from '@orders/components/FilterButton';

import utilsColumns from './utils/columns';

import { Container, FilterButtonContainer } from './styledComponents';
import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { PeriodDatePicker } from '@commons/DatePickers/PeriodDatePicker';

const OrderMappingsModal = (props) => {
  const {
    invoiceControlList: { store, supplier, id: invoiceControlListId, invoiceDate },
    showErrorMessage,
    setOrdersToBeAdded,
    alreadyMappedOrdersKeyById = {},
    // Redux
    client: { storeName },
  } = props;

  const theme = getTheme();

  // List view states
  const [filters, setFilters] = useState(null);
  const [applyFilters, setApplyFilters] = useState(true);
  const [advancedFilters, setAdvancedFilters] = useState(null);
  const [listViewColumns] = useState(utilsColumns.get(store, supplier, storeName));

  // Data states
  const [isLoading, setIsLoading] = useState(true);
  const [availableOrders, setAvailableOrders] = useState([]);
  const [displayedOrders, setDisplayedOrders] = useState([]);

  const periodPickerState = usePeriodDatePickerState(
    moment(invoiceDate || undefined).subtract(1, 'month'),
    moment(invoiceDate || undefined).add(1, 'month'),
  );

  // Loading
  useEffect(() => {
    if (!periodPickerState.startDate || !periodPickerState.endDate) {
      return;
    }

    setOrdersToBeAdded([]);

    loadOrders();
  }, [periodPickerState.startDate, periodPickerState.endDate]);

  // Handle filters
  useEffect(() => {
    if (!applyFilters) {
      return;
    }

    if (!advancedFilters || !advancedFilters.length) {
      return setDisplayedOrders(availableOrders);
    }

    const filteredOrders = advancedFilters.reduce(
      (result, { doFilter, propertyKey, value }) => doFilter(result, propertyKey, value),
      availableOrders,
    );

    setDisplayedOrders(filteredOrders);
  }, [applyFilters, advancedFilters, availableOrders]);

  const loadOrders = async () => {
    setIsLoading(true);

    try {
      const formattedStartDate = periodPickerState.startDate.format(
        DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY,
      );
      const formattedEndDate = periodPickerState.endDate.format(
        DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY,
      );

      const orders = await invoiceService.getAvailableOrdersToAssociate(
        store.id,
        supplier.id,
        invoiceControlListId,
        formattedStartDate,
        formattedEndDate,
      );

      const formattedOrders = orders.map((order) => ({
        ...order,
        isNotSelectable: order.associatedInvoice, // already associated order should be disable
      }));

      setAvailableOrders(formattedOrders);
    } catch {
      setAvailableOrders([]);

      showErrorMessage(
        i18next.t('INVOICE.INVOICE_CONTROL_DETAILS.ORDER_MAPPINGS_ASSOCIATION_FETCH_ERROR'),
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container>
      <ListView
        columns={listViewColumns}
        data={displayedOrders}
        defaultOrderBy={'startOrderDate'}
        defaultOrderType={'desc'}
        isLoading={isLoading}
        markerConfiguration={{
          isHidden: ({ id }) => !alreadyMappedOrdersKeyById[id],
          backgroundColor: theme.colors.brand.secondary,
          icon: { src: '/images/inpulse/central-black-small.svg' },
        }}
        padding={LISTVIEW_NO_TOP_PADDING}
        renderEmptyState={() => <EmptyState label={i18next.t('GENERAL.NO_RESULT')} />}
        renderFilterButton={() => (
          <FilterButtonContainer>
            <PeriodDatePicker
              disabled={isLoading}
              endDate={periodPickerState.endDate}
              focusedDateInput={periodPickerState.focusedDateInput}
              setFocusedDateInput={periodPickerState.setFocusedDateInput}
              startDate={periodPickerState.startDate}
              onDatesChange={periodPickerState.handleSelectedDates}
            />
            <DeepsightFiltersButton
              advancedFilters={advancedFilters}
              applyFilters={applyFilters}
              columnsFilterList={listViewColumns.filter(({ filterType }) => !!filterType)}
              filters={filters}
              isLoading={isLoading}
              readOnly={isLoading}
              setAdvancedFilters={setAdvancedFilters}
              setApplyFilters={setApplyFilters}
              setFilters={setFilters}
              textFilterButton={i18next.t('GENERAL.LIST_VIEW_FILTER_BUTTON')}
            />
          </FilterButtonContainer>
        )}
        setSelectedItems={setOrdersToBeAdded}
        disableResetSelectionOnNavigation
        forceEnableSelection
        hideAllPerPageOption
      />
    </Container>
  );
};

const mapStateToProps = (state) => ({
  client: getClientInfo(state.baseReducer.user),
});

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(OrderMappingsModal);
